import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, Optional, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { map, mergeMap, take, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { State } from '../../../store';
import { LocationOption, GeoJSONType, RequestLink, RequestLinkType, RequestLocationAddress } from '../../models/request.model';
import { LocationSelectionComponent } from './location-selection/location-selection.component';
import { ValidatedRequestData, ValidateInformationComponent } from './validate-information/validate-information.component';
import { RequestInformationComponent } from './request-information/request-information.component';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import {
    GeoJSONLocationRequest,
    SubCategoryItem,
    CategoryItem,
    CreateRequestPayload,
    CreateRequestLinkPayload,
    CreateRequestLocationPayload,
    CreateRequestLocationAddressPayload,
} from '../../models/create-request.dto';
import { organisationSelector } from '../../../global/store/global.selectors';
import { Organisation } from '../../../global/global.model';
import { RequestsV2ImplService } from '../../services/requests-v2.impl.service';
import { profileSelector } from '../../../user/store/user.selectors';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { authConfigIdSelector } from 'src/app/auth/store/auth.selectors';
import { UserService } from '../../../user/service/user.abstract.service';
import { AdminRegisterPayload } from '../../../user/user.model';
import { emailRegex } from 'src/app/global/validators/match.validator';

@Component({
    selector: 'app-create-request-dialog',
    templateUrl: './create-request-dialog.component.html',
    styleUrls: ['./create-request-dialog.component.scss'],
})
export class CreateRequestDialogComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('locationComponent') locationComponent: LocationSelectionComponent | null = null;

    @ViewChild(ValidateInformationComponent) validateInfoComponent: ValidateInformationComponent | null = null;

    @ViewChild(RequestInformationComponent, { static: false }) requestInfoComponent: RequestInformationComponent | null = null;

    private destroy$ = new Subject<void>();

    private organisationCenterCoords: [number, number] | null = null;

    loading = true;

    submitting = false;

    currentStep = 0;

    readonly STEP_CHOOSE_CITIZEN = 0;

    readonly STEP_CATEGORY = 1;

    readonly STEP_SUBCATEGORY = 2;

    readonly STEP_LOCATION = 3;

    readonly STEP_REQUEST_INFO = 4;

    readonly STEP_VALIDATION = 5;

    readonly STEP_REQUEST_SENT = 6;

    selectedCategory: CategoryItem | null = null;

    selectedCitizen: any | null = null;

    selectedSubcategory: SubCategoryItem | null = null;

    selectedLocation: LocationOption | null = null;

    selectedLocationData: GeoJSONLocationRequest | null = null;

    hasSelectedLocation = false;

    requestTitle = '';

    requestDescription = '';

    requestFiles: File[] = [];

    requestLinks: RequestLink[] = [];

    attachedFiles: any[] = [];

    createdRequestId = '';
    createdRequestObjectId = '';
    preLinkedSourceRequestId = '';

    isPublicRequest = false;

    categories: CategoryItem[] = [];

    currentUser: any | null = null;

    isCreatingCitizen = false;
    newCitizenForm: FormGroup;
    creatingCitizen = false;

    constructor(
        private router: Router,
        private dialogRef: MatDialogRef<CreateRequestDialogComponent>,
        private requestsService: RequestsV2ImplService,
        private translate: TranslateService,
        private messageService: MessageService,
        private store: Store<State>,
        private userService: UserService,
        private fb: FormBuilder,
        @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: any,
    ) {
        this.dialogRef.updateSize('80vw', '90vh');

        window.addEventListener('resize', () => {
            if (window.innerWidth <= 768) {
                this.dialogRef.updateSize('100vw', '100vh');
                this.dialogRef.addPanelClass('fullscreen-dialog');
            } else {
                this.dialogRef.updateSize('80vw', '90vh');
            }
        });

        if (window.innerWidth <= 768) {
            this.dialogRef.updateSize('100vw', '100vh');
            this.dialogRef.addPanelClass('fullscreen-dialog');
        }
    }

    ngOnInit(): void {
        this.loading = true;

        // Get organization bounds from store
        this.store
            .select(organisationSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((organisation: Organisation) => {
                if (organisation?.bounds?.center) {
                    // Set coordinates from organization center
                    this.organisationCenterCoords = [organisation.bounds.center.lon, organisation.bounds.center.lat];
                }
            });

        this.store
            .select(profileSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((profile) => {
                this.currentUser = profile;
            });

        this.initializePreLinkedRequest();
        this.loadCategories();
        // this.loadContactInfo();
    }

    private initializePreLinkedRequest(): void {
        const preLinkedRequest: RequestLink['linkedRequest'] | undefined = this.dialogData?.preLinkedRequest;
        const preLinkedType: RequestLinkType | undefined = this.dialogData?.preLinkedType;

        if (!preLinkedRequest?._id) {
            return;
        }

        this.preLinkedSourceRequestId = preLinkedRequest.requestId || '';

        this.requestLinks = [
            {
                type: preLinkedType || RequestLinkType.IS_LINKED_TO,
                linkedRequest: preLinkedRequest,
                linkedRequestId: preLinkedRequest._id,
            },
        ];
    }

    get openedFromLinkingFlow(): boolean {
        return !!this.preLinkedSourceRequestId;
    }

    loadCategories(): void {
        this.requestsService
            .getRequestCategories()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (response && response.length > 0) {
                        // Map to CategoryItem and SubCategoryItem
                        this.categories = response.map((item: any) => {
                            const subCategories: SubCategoryItem[] = [];
                            if (item.subCategories && Array.isArray(item.subCategories)) {
                                item.subCategories.forEach((sub: any) => {
                                    subCategories.push({
                                        _id: sub._id || '',
                                        name: sub.name || '',
                                        slaInDays: sub.slaInDays || null,
                                        selected: false,
                                    });
                                });
                            }

                            // Simpler mapping: Add selected and cast
                            (item as CategoryItem).selected = false;
                            (item as CategoryItem).subCategories = subCategories; // Assign mapped subcategories
                            return item as CategoryItem;
                        });
                    }
                    this.loading = false;
                },
                error: (_error) => {
                    this.loading = false;
                },
            });
    }

    onCategorySelected(category: CategoryItem): void {
        if (this.selectedCategory) {
            this.selectedCategory.selected = false;
        }

        this.selectedCategory = category;
        category.selected = true;

        this.next();
    }

    onCitizenSelected(users: any[]): void {
        if (users && users.length > 0) {
            const user = users[0];
            this.selectedCitizen = {
                ...user,
                picture: user.picture || user.avatar,
            };
        } else {
            this.selectedCitizen = null;
        }
    }

    selectCurrentUser(): void {
        if (this.currentUser) {
            this.selectedCitizen = this.currentUser;
        }

        this.next();
    }

    isCurrentUserSelected(): boolean {
        if (!this.selectedCitizen || !this.currentUser) {
            return false;
        }
        return this.selectedCitizen.id === this.currentUser.id || this.selectedCitizen._id === this.currentUser.id;
    }

    onSubcategorySelected(subcategory: SubCategoryItem): void {
        this.selectedSubcategory = subcategory;

        this.next();
    }

    onLocationSelectionChanged(hasSelection: boolean): void {
        this.hasSelectedLocation = hasSelection;
    }

    onLocationSelected(location: LocationOption): void {
        this.selectedLocation = location;
        // No need for hasSelectedLocation as it's set by onLocationSelectionChanged
    }

    onLocationRequestDataChange(locationData: GeoJSONLocationRequest): void {
        this.selectedLocationData = locationData;
    }

    onPublicStatusChanged(isPublic: boolean): void {
        this.isPublicRequest = isPublic;
    }

    onRequestInfoSubmitted(requestInfo: any): void {
        this.requestDescription = requestInfo.description;
        this.requestFiles = [...requestInfo.files];
        this.requestLinks = [...(requestInfo.links || [])];
        this.attachedFiles = requestInfo.files.map((file: File) => ({
            name: file.name,
            size: file.size,
            type: file.type,
            file,
            preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        }));
        this.onStepChange(this.STEP_VALIDATION);
    }

    closeDialog(): void {
        if (this.createdRequestObjectId || this.createdRequestId) {
            this.dialogRef.close({
                refresh: true,
                createdRequestId: this.createdRequestId,
                createdRequestObjectId: this.createdRequestObjectId,
            });
            return;
        }

        this.dialogRef.close();
    }

    onStepChange(step: number): void {
        this.currentStep = step;

        if (step === this.STEP_REQUEST_INFO) {
            setTimeout(() => {
                if (this.requestInfoComponent) {
                    this.requestInfoComponent.initializeFormData();
                }
            });
        }
    }

    goBack(): void {
        if (this.currentStep === this.STEP_VALIDATION) {
            this.onStepChange(this.STEP_REQUEST_INFO);
        } else if (this.currentStep === this.STEP_REQUEST_INFO) {
            this.onStepChange(this.STEP_LOCATION);
        } else if (this.currentStep === this.STEP_LOCATION) {
            if (this.selectedCategory && (!this.selectedCategory.subCategories || this.selectedCategory.subCategories.length === 0)) {
                this.onStepChange(this.STEP_CATEGORY);
            } else {
                this.onStepChange(this.STEP_SUBCATEGORY);
            }
        } else if (this.currentStep === this.STEP_SUBCATEGORY) {
            this.onStepChange(this.STEP_CATEGORY);
        } else if (this.currentStep === this.STEP_CATEGORY) {
            this.onStepChange(this.STEP_CHOOSE_CITIZEN);
        }
    }

    goToStep(step: number): void {
        if (step < this.currentStep) {
            // Special handling for jumping back to category/subcategory to ensure consistency
            if (step === this.STEP_CATEGORY) {
                // If we go back to category, we might need to reset subcategory if we change category?
                // For now, just navigation.
                this.onStepChange(step);
            } else if (step === this.STEP_SUBCATEGORY) {
                // Only allow going to subcategory if we have a category selected and it has subcategories
                if (this.selectedCategory && this.selectedCategory.subCategories && this.selectedCategory.subCategories.length > 0) {
                    this.onStepChange(step);
                } else {
                    // If no subcategories, go to category instead
                    this.onStepChange(this.STEP_CATEGORY);
                }
            } else {
                this.onStepChange(step);
            }
        }
    }

    next(): void {
        switch (this.currentStep) {
            case this.STEP_CHOOSE_CITIZEN:
                this.onStepChange(this.STEP_CATEGORY);
                break;

            case this.STEP_CATEGORY:
                if (this.selectedCategory && (!this.selectedCategory.subCategories || this.selectedCategory.subCategories.length === 0)) {
                    this.selectedSubcategory = null;
                    this.onStepChange(this.STEP_LOCATION);
                } else {
                    this.onStepChange(this.STEP_SUBCATEGORY);
                }
                break;

            case this.STEP_SUBCATEGORY:
                this.onStepChange(this.STEP_LOCATION);
                break;

            case this.STEP_LOCATION:
                if (this.hasSelectedLocation && this.locationComponent) {
                    const selectedLocation = this.locationComponent.getSelectedLocation();
                    if (selectedLocation) {
                        this.selectedLocation = selectedLocation;
                        this.selectedLocationData = this.locationComponent.getLocationRequestData();
                        this.locationComponent.proceedWithSelectedLocation();
                    }
                    this.onStepChange(this.STEP_REQUEST_INFO);
                }
                break;

            case this.STEP_REQUEST_INFO:
                if (this.requestInfoComponent) {
                    this.requestInfoComponent.goToNextStep();
                } else {
                    const requestInfo = {
                        description: this.requestDescription,
                        files: this.requestFiles || [],
                        links: this.requestLinks || [],
                    };
                    this.onRequestInfoSubmitted(requestInfo);
                }
                break;

            case this.STEP_VALIDATION:
                this.submitRequest();
                break;

            default:
        }
    }

    onRequestValidated(validationData: ValidatedRequestData): void {
        this.isPublicRequest = validationData.isPublicRequest;

        // If we received uploaded file URLs, update the attachedFiles
        if (validationData.uploadedFileUrls && validationData.uploadedFileUrls.length > 0) {
            // Update the attachedFiles with the URLs from validation component
            this.updateAttachedFilesWithUrls(validationData.uploadedFileUrls);
        }

        this.submitRequest();
    }

    private updateAttachedFilesWithUrls(fileUrls: string[]): void {
        // Find files that need URLs
        const filesNeedingUrls = this.attachedFiles.filter((file) => file.file && file.file instanceof File);

        // Assign URLs to files (assuming the order matches)
        filesNeedingUrls.forEach((file, index) => {
            if (index < fileUrls.length) {
                file.url = fileUrls[index];
            }
        });
    }

    skipLocation(): void {
        this.selectedLocation = null;
        this.selectedLocationData = null;
        this.onStepChange(this.STEP_REQUEST_INFO);
    }

    submitRequest(): void {
        this.submitting = true;

        const locationData = this.selectedLocationData || this.createDefaultLocationDto();

        // Create an array of file objects from attachedFiles if they have URLs
        const filesUrls: { url: string; originalName: string }[] = this.attachedFiles.filter((file) => file.url).map((file) => ({ url: file.url, originalName: file.name }));
        const links: CreateRequestLinkPayload[] = this.requestLinks.reduce((acc, link) => {
            const linkedRequestId = link.linkedRequestId || link.linkedRequest?._id;

            if (linkedRequestId) {
                acc.push({
                    type: link.type,
                    linkedRequestId,
                });
            }

            return acc;
        }, [] as CreateRequestLinkPayload[]);

        const requestPayload: CreateRequestPayload = {
            description: this.requestDescription,
            category: this.selectedCategory?._id || '',
            subCategory: this.selectedSubcategory?._id || undefined,
            location: this.createLocationPayload(locationData),
            files: filesUrls,
            isPrivate: !this.isPublicRequest,
            userId: this.selectedCitizen?.id || this.selectedCitizen?._id,
            links,
        };

        this.requestsService
            .postRequest(requestPayload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.submitting = false;
                    this.createdRequestId = response?.requestId || '';
                    this.createdRequestObjectId = response?._id || '';
                    this.currentStep = this.STEP_REQUEST_SENT;
                },
                error: (error) => {
                    this.handleRequestError(error);
                },
            });
    }

    private createLocationPayload(locationData: GeoJSONLocationRequest): CreateRequestLocationPayload {
        return {
            geometry: locationData.geometry,
            address: this.createLocationAddressPayload(locationData.properties.address),
            entireStreet: locationData.entireStreet,
        };
    }

    private createLocationAddressPayload(address: RequestLocationAddress): CreateRequestLocationAddressPayload {
        return {
            street: this.toAddressString(address.street || address.streetName),
            streetName: this.toAddressString(address.streetName || address.street),
            number: this.toAddressString(address.number),
            postCode: this.toAddressString(address.postCode),
            city: this.toAddressString(address.city),
            province: this.toAddressString(address.province),
            country: address.country ? this.toAddressString(address.country) : undefined,
            typeStreet: this.toAddressString(address.typeStreet),
            direction: this.toAddressString(address.direction),
            unit: this.toAddressString(address.unit),
        };
    }

    private toAddressString(value: unknown): string {
        return value == null ? '' : `${value}`;
    }

    private createDefaultLocationDto(): GeoJSONLocationRequest {
        const defaultCoords = this.organisationCenterCoords;
        const defaultAddress: RequestLocationAddress = {
            formattedAddress: `${defaultCoords[1]}, ${defaultCoords[0]}`, // lat, lng format
            street: '',
            streetName: '',
            number: '',
            postCode: '',
            city: '',
            province: '',
            typeStreet: '',
            direction: '',
            unit: '',
        };

        return {
            type: 'Feature',
            properties: {
                name: this.translate.instant('create-request-dialog.default-location-name'),
                type: 'custom',
                address: defaultAddress,
            },
            geometry: {
                type: GeoJSONType.Point,
                coordinates: defaultCoords,
            },
        };
    }

    private handleRequestError(error: any): void {
        let errorMessage = this.translate.instant('create-request-dialog.errors.generic');

        if (error.status === 400) {
            errorMessage = this.translate.instant('create-request-dialog.errors.invalid-data');
        } else if (error.status === 401) {
            errorMessage = this.translate.instant('create-request-dialog.errors.unauthorized');
        } else if (error.status === 500) {
            errorMessage = this.translate.instant('create-request-dialog.errors.server');
        }

        this.messageService.error(errorMessage);
        this.submitting = false;
    }

    canProceed(): boolean {
        if (this.currentStep === this.STEP_CHOOSE_CITIZEN) {
            return !!this.selectedCitizen;
        }
        if (this.currentStep === this.STEP_CATEGORY) {
            return !!this.selectedCategory;
        }
        if (this.currentStep === this.STEP_SUBCATEGORY) {
            return !!this.selectedSubcategory;
        }
        if (this.currentStep === this.STEP_LOCATION) {
            return this.hasSelectedLocation;
        }
        if (this.currentStep === this.STEP_REQUEST_INFO) {
            if (this.requestInfoComponent) {
                return this.requestInfoComponent.isFormValid();
            }
            if (this.requestDescription) {
                const words = this.requestDescription.trim().split(/\s+/);
                const wordCount = this.requestDescription.trim() ? words.length : 0;
                return this.requestDescription.trim() !== '' && wordCount <= 300;
            }
            return false;
        }
        if (this.currentStep === this.STEP_VALIDATION) {
            return true;
        }
        return false;
    }

    createOnly(): void {
        if (this.validateInfoComponent) {
            this.validateInfoComponent.validateAndProceed();
        } else {
            this.submitRequest();
        }
    }

    createAndDispatch(): void {
        this.submitRequest();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            if (this.currentStep === this.STEP_REQUEST_INFO && this.requestInfoComponent) {
                this.requestInfoComponent.initializeFormData();
            }
        });
    }

    closeRequestSent(): void {
        this.dialogRef.close({
            refresh: true,
            createdRequestId: this.createdRequestId,
            createdRequestObjectId: this.createdRequestObjectId,
        });
    }

    onCitizenCreateClick(searchTerm: string = ''): void {
        this.isCreatingCitizen = true;

        let initialEmail = '';
        let initialPhone = '';

        if (searchTerm) {
            if (searchTerm.includes('@')) {
                initialEmail = searchTerm;
            } else if (/\d/.test(searchTerm)) {
                initialPhone = searchTerm;
            }
        }

        this.newCitizenForm = this.fb.group({
            firstName: ['', [Validators.required]],
            lastName: ['', [Validators.required]],
            email: [initialEmail, [Validators.required, Validators.pattern(emailRegex)]],
            phone: [initialPhone, [Validators.minLength(14), Validators.maxLength(14)]],
        });
    }

    cancelCitizenCreation(): void {
        this.isCreatingCitizen = false;
        this.newCitizenForm = null;
    }

    submitNewCitizen(): void {
        if (this.newCitizenForm.invalid) {
            this.newCitizenForm.markAllAsTouched();
            return;
        }

        this.creatingCitizen = true;
        const formValue = this.newCitizenForm.value;

        this.store
            .select(authConfigIdSelector)
            .pipe(take(1))
            .subscribe((clientId) => {
                const registerPayload: AdminRegisterPayload = {
                    firstName: formValue.firstName,
                    lastName: formValue.lastName,
                    email: formValue.email,
                    orgId: environment.OIDC_ORG_ID,
                    clientId: clientId || undefined,
                };

                this.userService
                    .adminRegisterUser(registerPayload)
                    .pipe(
                        mergeMap((response) => {
                            if (!response?.userId) {
                                throw new Error('No userId returned');
                            }
                            // Patch phone if provided
                            if (formValue.phone) {
                                return this.userService.updateUserRecordById(response.userId, { phone: formValue.phone }).pipe(map(() => response));
                            }
                            return of(response);
                        }),
                        takeUntil(this.destroy$),
                    )
                    .subscribe({
                        next: (response) => {
                            this.handleUserCreated(response.userId, formValue.email);
                        },
                        error: (error) => {
                            this.creatingCitizen = false;
                            if (error.status === 409) {
                                this.messageService.error(this.translate.instant('request_v2.create-request-dialog.errors.email-already-used'));
                            } else {
                                this.messageService.error(this.translate.instant('request_v2.create-request-dialog.errors.user-creation-failed'));
                            }
                        },
                    });
            });
    }

    private handleUserCreated(userId: string, email: string): void {
        let searchTerm = email;
        if (!searchTerm) {
            // If no email, get user by ID directly
            this.userService.getUser(userId).subscribe((user) => {
                this.selectedCitizen = {
                    id: user._id,
                    name: `${user.firstName} ${user.lastName}`,
                    email: user.email,
                    phone: user.phone,
                    picture: (user as any).picture,
                };
                this.isCreatingCitizen = false;
                this.creatingCitizen = false;
            });
            return;
        }

        this.userService
            .quickSearch(searchTerm, 1, 10, 'email')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.creatingCitizen = false;
                    if (result && result.docs && result.docs.length > 0) {
                        const user = result.docs.find((u) => u._id === userId) || result.docs[0];

                        this.selectedCitizen = {
                            id: user._id,
                            name: `${user.firstName} ${user.lastName}`,
                            email: user.email,
                            phone: user.phone,
                            picture: user.picture,
                        };

                        this.isCreatingCitizen = false;
                    } else {
                        // Fallback to get user by ID if search fails
                        this.userService.getUser(userId).subscribe((u) => {
                            this.selectedCitizen = {
                                id: u._id,
                                name: `${u.firstName} ${u.lastName}`,
                                email: u.email,
                                phone: u.phone,
                                picture: (u as any).picture,
                            };
                            this.isCreatingCitizen = false;
                        });
                    }
                },
                error: () => {
                    this.creatingCitizen = false;
                    this.isCreatingCitizen = false;
                },
            });
    }
}
